import { db } from '@/config/firebase';
import { collection, addDoc, updateDoc, doc, query, where, getDocs, orderBy, Timestamp, getDoc } from 'firebase/firestore';
import { StudentPayment } from '@/types';

export class PaymentService {
  private readonly COLLECTION = 'studentPayments';

  async createPayment(paymentData: Omit<StudentPayment, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      console.log('Creating payment in collection:', this.COLLECTION);
      console.log('Payment data:', paymentData);
      
      const paymentRef = await addDoc(collection(db, this.COLLECTION), {
        ...paymentData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      console.log('Payment created with ID:', paymentRef.id);
      return paymentRef.id;
    } catch (error) {
      console.error('Error creating payment:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to create payment');
    }
  }

  async updatePaymentMethod(paymentId: string, method: string): Promise<void> {
    try {
      const paymentRef = doc(db, this.COLLECTION, paymentId);
      await updateDoc(paymentRef, {
        paymentMethod: method,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating payment method:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update payment method');
    }
  }

  async updatePaymentStatus(paymentId: string, status: string, errorMessage?: string): Promise<void> {
    try {
      const paymentRef = doc(db, this.COLLECTION, paymentId);
      const updateData: any = {
        status,
        updatedAt: Timestamp.now()
      };

      if (errorMessage) {
        updateData.errorMessage = errorMessage;
      }

      await updateDoc(paymentRef, updateData);
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update payment status');
    }
  }

  async markCashPaymentAsPaid(paymentId: string, adminId: string, adminName: string): Promise<void> {
    const paymentRef = doc(db, this.COLLECTION, paymentId);
    await updateDoc(paymentRef, {
      status: 'paid',
      adminId,
      adminName,
      paymentDate: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  }

  async getStudentPayments(studentId: string): Promise<StudentPayment[]> {
    const q = query(
      collection(db, this.COLLECTION),
      where('studentId', '==', studentId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate(),
      paymentDate: doc.data().paymentDate?.toDate()
    })) as StudentPayment[];
  }

  async getPendingCashPayments(): Promise<StudentPayment[]> {
    const q = query(
      collection(db, this.COLLECTION),
      where('status', '==', 'pending'),
      where('paymentMethod', '==', 'cash'),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate(),
      paymentDate: doc.data().paymentDate?.toDate()
    })) as StudentPayment[];
  }

  async getEnrollmentPayment(enrollmentId: string): Promise<StudentPayment | null> {
    const q = query(
      collection(db, this.COLLECTION),
      where('enrollmentId', '==', enrollmentId)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate(),
      paymentDate: doc.data().paymentDate?.toDate()
    } as StudentPayment;
  }

  async updateExistingPaymentsWithInstructorId(): Promise<void> {
    try {
      console.log('Starting payment update process...');
      
      // 1. Get all payments
      const paymentsRef = collection(db, this.COLLECTION);
      const paymentsQuery = query(paymentsRef);
      const paymentsSnapshot = await getDocs(paymentsQuery);
      
      console.log(`Found ${paymentsSnapshot.size} payments to process`);
      
      // 2. Process each payment
      for (const paymentDoc of paymentsSnapshot.docs) {
        const payment = paymentDoc.data();
        const paymentId = paymentDoc.id;
        
        // Skip if already has instructorId
        if (payment.instructorId) {
          console.log(`Payment ${paymentId} already has instructorId: ${payment.instructorId}`);
          continue;
        }
        
        // Get instructorId from enrollment
        if (payment.enrollmentId) {
          const enrollmentRef = doc(db, 'enrollments', payment.enrollmentId);
          const enrollmentDoc = await getDocs(query(collection(db, 'enrollments'), where('id', '==', payment.enrollmentId)));
          
          if (!enrollmentDoc.empty) {
            const enrollment = enrollmentDoc.docs[0].data();
            if (enrollment.instructorId) {
              // Update payment with instructorId
              await updateDoc(doc(db, this.COLLECTION, paymentId), {
                instructorId: enrollment.instructorId
              });
              console.log(`Updated payment ${paymentId} with instructorId: ${enrollment.instructorId}`);
            } else {
              console.warn(`Enrollment ${payment.enrollmentId} has no instructorId`);
            }
          } else {
            console.warn(`No enrollment found for payment ${paymentId}`);
          }
        } else {
          console.warn(`Payment ${paymentId} has no enrollmentId`);
        }
      }
      
      console.log('Payment update process completed!');
    } catch (error) {
      console.error('Error updating payments:', error);
      throw error;
    }
  }
}

export const paymentService = new PaymentService(); 